import Button from './ui/Button';
import React, { memo } from 'react';
import { Search, CheckCircle, Users } from 'lucide-react';
import PropTypes from 'prop-types';
// Match-Karte: zurueck auf V2 (Aug 2026) — der V3-Kopf gefiel im Vergleich nicht.
// V3 bleibt als Datei liegen; Wechsel ist genau diese eine Zeile.
import { ProfileCardV2 as ProfileCard } from './ProfileCardV2';
import LoadingSpinner from './ui/LoadingSpinner';
import Skeleton from './ui/Skeleton.jsx';
import PageShell from './ui/PageShell';
import EmptyState from './ui/EmptyState.jsx';
const HomeView = memo(({
  potentialMatches,
  totalProfileCount,
  isLoading,
  error,
  isLoadingMore,
  hasMoreProfiles,
  userProfile,
  onConnect,
  onLoadMore,
  onOpenSearch
}) => {
  // Kein eigener Refresh-Indikator: Pull-to-Refresh wurde September 2026
  // entfernt, der Desktop-Wheel-Refresh laeuft ueber isRefreshing in MainScreen.
  const showBlockingSpinner = isLoading && potentialMatches.length === 0;

  return (
    <PageShell
      variant="main"
      embedded
      title="Matches"
      meta={totalProfileCount > 0 ? `${potentialMatches.length}/${totalProfileCount}` : undefined}
      actions={(
        <button
          type="button"
          onClick={onOpenSearch}
          className="relative p-2 rounded-xl bg-surface-raised hover:bg-foreground/10 text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          title="Suche"
          aria-label="Suche öffnen"
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    >
      {showBlockingSpinner && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true">
          <span className="sr-only">Profile werden geladen…</span>
          {[0, 1, 2].map((i) => <Skeleton.Card key={i} />)}
        </div>
      )}

      {!showBlockingSpinner && (
        <>
          {error && (
            <div className="mb-4 rounded-xl border border-danger/40 bg-danger-soft p-3 sm:p-4 text-foreground" role="alert">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {potentialMatches.length > 0 ? (
              potentialMatches.map(profile => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  currentUser={userProfile}
                  onConnect={onConnect}
                  origin="ROUND"
                />
              ))
            ) : (
              <EmptyState
                className="col-span-full"
                icon={Users}
                title="Keine Profile gefunden"
                text="Sobald sich mehr Lernpartner:innen an deiner Schule anmelden, tauchen sie hier auf."
              />
            )}
          </div>

          {/* Infinite Scroll Loading */}
          {isLoadingMore && (
            <div className="py-6">
              <LoadingSpinner size="small" message="Weitere Profile werden geladen..." />
            </div>
          )}

          {/* Manual Load More Button */}
          {hasMoreProfiles && !isLoadingMore && potentialMatches.length > 0 && (
            <div className="text-center py-6">
              <Button onClick={onLoadMore} className="mx-auto">
                <span>Weitere Profile laden</span>
                {/* `text-white/70` war fest verdrahtet — auf der CTA-Flaeche heisst
                    die Schriftfarbe `cta-foreground` und dreht mit dem Theme mit. */}
                <span className="text-cta-foreground/70">({potentialMatches.length}/{totalProfileCount})</span>
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                Oder scrolle nach unten für automatisches Laden
              </p>
            </div>
          )}

          {/* End of List Indicator */}
          {!hasMoreProfiles && potentialMatches.length > 0 && totalProfileCount > 20 && (
            <div className="text-center py-6">
              <div className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface-raised p-3 sm:p-4">
                <CheckCircle className="h-5 w-5 text-success" aria-hidden="true" />
                <span className="text-sm font-medium">
                  Alle {totalProfileCount} Profile geladen
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
});

HomeView.displayName = 'HomeView';

HomeView.propTypes = {
  potentialMatches: PropTypes.array.isRequired,
  totalProfileCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isLoadingMore: PropTypes.bool.isRequired,
  hasMoreProfiles: PropTypes.bool.isRequired,
  userProfile: PropTypes.object.isRequired,
  onConnect: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired
};

export default HomeView;
